import { Fragment, useContext, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, FAB } from '@upmc/ims.component-library';

import axiosInstance from '../../../services/axios/axios';
import { useHistory } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { UserContext } from 'context/User/UserContext';
import { useQuery } from '../../../utils/hooks/useQuery';
import { getLegacyPath } from 'routes/_utils';
import {
	addRequesteesByFilter,
	advance,
	clearData,
	deleteRow,
	filterRows,
	indexRows,
	init,
	setDirectReports,
	setLoading,
	setRows,
	sortByHeader,
	toggleRow,
	toggleRows,
	updateRowValue,
} from '../../../store/requestFlow/actions';
import { addMessage, clearMessages } from '../../../store/ancillary/actions';
import { setSession } from '../../../store/utility';
import { checkValidity } from '../../../store/requestFlow/_utility';
import Base from '../../Base/Base';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import PersonSearch from '../../../components/UI/Input/PersonSearch/PersonSearch';
import { DataTable } from '../../../components/UI/MDC/DataTable/DataTable';
import { RequesteeSelections } from './RequesteeSelections/RequesteeSelections';
import { Actions } from '../../../components/UI/MDC/Card/Card';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Icon from '../../../components/UI/MDC/Icon/Icon';
import Switch from '@material/react-switch';
import { Subtitle1 } from '../../../components/UI/MDC/Typography/Typography';
import { Tooltip } from '../../../components/UI/MDC/Tooltip/Tooltip';
import { CONTENT_ALIGNMENT } from '../../../components/UI/MDC/Tooltip/_types';
import { ACTION_TYPES } from '../../../components/UI/MDC/DataTable/_types';
import {
	CELL_KEYS,
	CSS_CLASSES,
	ELEMENT_NAMES,
	HEADERS,
	IDENTIFIER_TYPES,
	VALIDATION_THRESHOLD,
} from './_constants';
import * as REQUEST_PARAMS from '../../../constants/requestConstants';
import { RequestTypes as REQUEST_TYPES } from '../../../constants/enums';

import classes from './RequesteeSearch.module.scss';
import { UserUpload } from './UserUpload';
import { ACTION_FILTER_TYPES } from '../../../store/requestFlow/_actionTypes';
import { deepCloneObject, WrapImsPacket } from '../../../store/utility';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { formatForID } from '../../../utils/strings/StringUtil';

const RequesteeSearch = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const EMPLOYEE_MANAGER_ROLE = 13;
	const NON_EMPLOYEE_MANAGER_ROLE = 281;
	const ACCOUNT_REQUESTOR_ROLE = 15;

	//The currently logged in user is considered a site administrator if and only if:
	//1. they have the site administrator role or templates in Epic
	//2. they do not have a manager role
	//3. they do not have an account requestor role
	//4. they are a non-employee
	const isSiteAdmin =
		user.IsSiteAdministrator &&
		!user.Roles?.some(r =>
			[
				EMPLOYEE_MANAGER_ROLE,
				ACCOUNT_REQUESTOR_ROLE,
				NON_EMPLOYEE_MANAGER_ROLE,
			].includes(r.ID),
		) &&
		user.IsNonEmployee;

	const rActions = useSelector(state => state.requestFlow.actions);
	const rAdvance = useSelector(state => state.requestFlow.advance);
	const rDirectReports = useSelector(
		state => state.requestFlow.directReports,
	);
	const rHeaders = useSelector(state => state.requestFlow.headers);
	const rLoading = useSelector(state => state.requestFlow.loading);
	const rRequestees = useSelector(state => state.requestFlow.requestees);
	const rRows = useSelector(state => state.requestFlow.rows);

	const [sErrorMessage, setErrorMessage] = useState(); // PersonSearch error message
	const [sIsSearchLoading, setIsSearchLoading] = useState(false); // toggle PersonSearch loading animation
	const [sIsRetiredChecked, setIsRetiredChecked] = useState(true); // manage PersonSearch Switch input option
	const [sSelectedRequestees, setSelectedRequestees] = useState(rRequestees); // manage positive selections in internal requestee array
	const [sSuppressEmail, setSuppressEmail] = useState(false);
	const [sTabValue, setTabValue] = useState(0);
	const [sTabIcons, setTabIcons] = useState([
		{ key: 'search', value: 'person_search' },
		{ key: 'upload', value: 'file_upload' },
	]);
	const [invalidPopupOpen, setInvalidPopupOpen] = useState({
		valid: true,
		errorMessage: '',
		errorSubMessage: '',
	});

	const iconLegend = [
		{
			BindTo: rActions.findIndex(
				action => action.Type === ACTION_TYPES.TOGGLE,
			),
			ValuePrimary: 'Select user',
			ValueSecondary: 'Deselect user',
		},
		{
			BindTo: rActions.findIndex(
				action => action.Type === ACTION_TYPES.FLAG_PROBLEM,
			),
			ValuePrimary: 'Retired user',
		},
		{
			BindTo: rActions.findIndex(
				action => action.Type === ACTION_TYPES.FLAG_UPDATE,
			),
			ValuePrimary: 'Resend staff agreement',
		},
		{
			BindTo: rActions.findIndex(
				action => action.Type === ACTION_TYPES.FLAG_PERSON,
			),
			ValuePrimary: 'Regulatory / Tenant Badge Only User',
		},
		{
			BindTo: rActions.findIndex(
				action => action.Type === ACTION_TYPES.FLAG_LOCK,
			),
			ValuePrimary: 'Disabled vendor',
		},
		{
			BindTo: rActions.findIndex(
				action => action.Type === ACTION_TYPES.REMOVE,
			),
			ValuePrimary: 'Delete user',
		},
		{
			BindTo: rActions.findIndex(
				action => action.Type === ACTION_TYPES.REVERIFY,
			),
			ValuePrimary: 'Reverify user',
		},
	];
	const multiselectHeaders = HEADERS.reduce((objHeaders, header) => {
		objHeaders[header.replace(/ /g, '_')] = header;
		return objHeaders;
	}, {});
	const directReportsHeaders = rHeaders.filter(
		header => header.Value !== 'Manager',
	);

	//#region Query parameters
	/**
	 * Type of application request specified in URL query parameter(s).
	 * @type {string}
	 */
	let requestType = useQuery().get(REQUEST_PARAMS.REQUEST_TYPE);
	if (requestType == null) {
		requestType = 'Application';
	}
	//#endregion

	/**
	 * Creates and assigns appropriate action cell for handling @param row's invalidity.
	 *
	 * @function
	 * @param {Object} row `DataTable` invalid row of interest.
	 * @returns {Object} updated object with action cell handling invalidity.
	 */
	const assignInvalidAction = useCallback(
		row => {
			let bindTo;
			let tooltip;
			const keysCell = Object.keys(row.Cells);
			if (
				row.Cells[CELL_KEYS.RETIRED].Validation.Key.toLowerCase() ===
				'false'
			) {
				bindTo = rActions.findIndex(
					action => action.Type === ACTION_TYPES.FLAG_PROBLEM,
				);
				tooltip = 'Recently retired. Click to reactivate.';
			} else if (keysCell.includes(CELL_KEYS.PENDING)) {
				bindTo = rActions.findIndex(
					action => action.Type === ACTION_TYPES.FLAG_UPDATE,
				);
				tooltip = 'Has not acknowledged agreement. Click to remind.';
			} else if (keysCell.includes(CELL_KEYS.RESTRICTED)) {
				bindTo = rActions.findIndex(
					action => action.Type === ACTION_TYPES.FLAG_PERSON,
				);
				tooltip = 'Regulatory user. Click to proceed individually.';
			} else if (keysCell.includes(CELL_KEYS.DENIED)) {
				bindTo = rActions.findIndex(
					action => action.Type === ACTION_TYPES.FLAG_PERSON,
				);
				tooltip =
					'Tenant Badge Only user. Click to proceed individually.';
			} else if (keysCell.includes(CELL_KEYS.DISABLED)) {
				bindTo = rActions.findIndex(
					action => action.Type === ACTION_TYPES.FLAG_LOCK,
				);
				tooltip = 'Vendor account disabled. Click to enable.';
			} else {
				bindTo = null;
				tooltip = null;
			}

			if (bindTo && tooltip) {
				const actionCell = {
					BindTo: bindTo,
					Checked: false,
					Key: nanoid(),
					Text: tooltip,
				};
				if (Array.isArray(row.ActionCells)) {
					row.ActionCells.push(actionCell);
				} else {
					row.ActionCells = [actionCell];
				}
			}
			return row;
		},
		[rActions],
	);

	/**
	 * Initial render lifecycle hook. Configures initial state of Redux store.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		dispatch(
			init({
				actions: [
					{
						Type: ACTION_TYPES.TOGGLE,
						onChangedHandler: (_, __, row) => {
							dispatch(toggleRow(row));
						},
						Header: {
							Checked: false,
							Key: '0',
							onChangedHandler: e => dispatch(toggleRows(e, 0)),
						},
					},
					{
						Type: ACTION_TYPES.FLAG_PROBLEM,
						onChangedHandler: (_, __, row) => {
							dispatch(setLoading(true));
							history.push(
								getLegacyPath(
									`/ESO/Reactivate_View.aspx?id=${row.Cells['FI_ID'].Value}`,
								),
								row,
							);
						},
					},
					{
						Type: ACTION_TYPES.FLAG_UPDATE,
						onChangedHandler: (_, __, row) => {
							dispatch(setLoading(true));
							history.push(
								getLegacyPath(
									'/ESO/RemindNEWithAgreementEmail.aspx',
								),
								row,
							);
						},
					},
					{
						Type: ACTION_TYPES.FLAG_LOCK,
						onChangedHandler: async (_, __, row) => {
							dispatch(setLoading(true));
							await setSession('id', row.Cells['FI_ID'].Value);
							history.push(
								getLegacyPath(
									'/RRS/UserVendorSupportTypeDisabled.aspx',
								),
								row,
							);
						},
					},
					{
						Type: ACTION_TYPES.FLAG_PERSON,
						onChangedHandler: (_, __, row) => {
							dispatch(setLoading(true));
							history.push({
								pathname: '/RequestFlow/Agreements',
								search:
									`?${REQUEST_PARAMS.REQUEST_TYPE}=` +
									(requestType.toLowerCase() ===
									'service_account'
										? REQUEST_TYPES.FOR_OTHERS_SERVICE_ACCOUNT
										: REQUEST_TYPES.SINGLE),
								state: row,
							});
						},
					},
					{
						Type: ACTION_TYPES.REMOVE,
						onChangedHandler: (_, __, row) => {
							dispatch(deleteRow(row));
						},
					},
					{
						Type: ACTION_TYPES.REVERIFY,
						onChangedHandler: async (_, __, row) => {
							//determine the identifier type
							let identifierKey = IDENTIFIER_TYPES.NETWORK_ID; //default to NTID
							let id = '';
							Object.values(IDENTIFIER_TYPES).every(idType => {
								const idKey = idType.replace(/ /g, '_');
								if (
									idKey in row.Cells &&
									row.Cells[idKey].Value
								) {
									identifierKey = idType;
									id = row.Cells[idKey].Value;
									id = id ? id.trim() : id;
									return false;
								}
								return true;
							});
							try {
								await onValidateHandler(
									identifierKey,
									[id],
									true,
								);
							} catch (error) {
								raiseErrorBanner(error);
							}
						},
					},
				],
				advance: false,
				directReports: [],
				headers: HEADERS.map((header, index) => {
					return {
						Value: header,
						props: {
							onSortChange: sortOrder =>
								dispatch(sortByHeader(header, sortOrder)),
							sort: index === 0 ? 1 : null,
						},
					};
				}),
				requestees: [],
				rows: [],
				loading: false,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Lifecycle hook on local variable change. Evaluates and takes action on logged-on user information once available during initial loading.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		/**
		 * Retrieves direct reports of requestor and processes for rendering, if any.
		 *
		 * @function
		 * @return void.
		 */
		async function initialize() {
			if (user.ID && rActions.length > 0 && rDirectReports?.length < 1) {
				try {
					if (isSiteAdmin) {
						dispatch(setLoading(true));
					}
					axiosInstance
						.get('api/ESO/DirectReports', {
							params: {
								esoID: user.ESOID,
								isSiteAdministrator: isSiteAdmin,
							},
						})
						.then(response => {
							if (response) {
								switch (response.status) {
									case 200:
										if (
											response.data &&
											response.data.payload
										) {
											const processedRows =
												response.data.payload.map(
													row => {
														if (
															!checkValidity(row)
														) {
															row =
																assignInvalidAction(
																	row,
																);
														} else {
															row.ActionCells = [
																{
																	BindTo: 0,
																	Checked: false,
																	Key: nanoid(),
																	Text: 'Toggle selection',
																},
															];
														}
														row.isDisplayed = true;
														return row;
													},
												);
											dispatch(
												setDirectReports(processedRows),
											);
											if (isSiteAdmin) {
												dispatch(
													setRows(processedRows),
												);
												setTabValue(processedRows.length > 0 ? 1 : 0);
											}
											setTabIcons(tabIcons => {
												return tabIcons.length < 3
													? [
															...tabIcons,
															{
																key: 'direct reports',
																value: 'people',
															},
														]
													: tabIcons;
											});
										} else {
											setDirectReports(null);
										}
										break;
									default:
										throw new Error('Error occurred');
								}
							}
						})
						.finally(() => {
							dispatch(setLoading(false));
						});
				} catch (error) {
					dispatch(clearMessages());
					dispatch(
						addMessage({
							Key: 3,
							Value:
								error && error.response && error.response.data
									? error.response.data.payload
									: error.message
										? error.message
										: error,
						}),
					);
				}
			}
		}
		initialize();
	}, [
		user,
		isSiteAdmin,
		rActions,
		rDirectReports,
		assignInvalidAction,
		dispatch,
	]);

	/**
	 * Tab navigation hook on local state change. Resets the error message on tab change.
	 *
	 * @function
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		setErrorMessage(null);
	}, [sTabValue]);

	/**
	 * Requestee selection hook on local state change. Stores array of selected requestees in non-reducer local state.
	 * Component logic leverages the filtered array of requestees enough to store in local state rather than performed in-line.
	 *
	 * @function
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		setSelectedRequestees(
			rRequestees.filter(requestee => requestee.isSelected === true),
		);
	}, [rRequestees]);

	/**
	 * Submission lifecycle hook on local state change.
	 *
	 * @function
	 * @return void. No clean-up performed.
	 */
	useEffect(() => {
		/**
		 * Handles selection submission. Maintains expected session entries, performs routing based on selection(s).
		 *
		 * @function
		 * @return void.
		 */
		const advance = async () => {
			dispatch(
				init({
					advance: false,
				}),
			);
			await setSession('Request_AppMode', 'Modify');
			const requestees = sSelectedRequestees;

			if (requestees.length === 1) {
				// single-user redirect
				const requestee = requestees[0];
				await prepareSingleUserSubmit(requestee);
				if (
					requestee['Employee_Status'].Value.toLowerCase() === 'false'
				) {
					// Non-Employee Requestee For Others Flow

					// Service Account Request
					if (requestType.toLowerCase() === 'service_account') {
						history.push({
							pathname: '/ExistingServiceAccount',
							state: requestee,
							Service_Account_Search: true,
						});
					}
					// Regular Application Request
					else {
						history.push({
							pathname: '/RequestFlow/Agreements',
							search:
								`?${REQUEST_PARAMS.REQUEST_TYPE}=` +
								REQUEST_TYPES.SINGLE,
						});
					}
				} else {
					// Employee Requestees For Others Flow:

					// Service Account Request
					if (requestType.toLowerCase() === 'service_account') {
						history.push({
							pathname: '/ExistingServiceAccount',
							state: requestee,
						});
					}
					// Regular Application Request
					else {
						history.push(
							getLegacyPath(
								'/App/Index?ID=' + requestee['FI_ID'].Value,
							),
							requestee,
						);
					}
				}
			} else {
				// multi-user redirect
				const arrID = requestees.map(
					requestee => requestee['FI_ID'].Value,
				);
				await setSession('UsersList', arrID.join(','));
				await setSession('SuppressEmail', sSuppressEmail ? '1' : null);

				if (
					requestees.some(requestee =>
						Object.keys(requestee).includes(CELL_KEYS.UNASSOCIATED),
					)
				) {
					history.push({
						pathname: '/RequestFlow/Agreements',
						search:
							`?${REQUEST_PARAMS.REQUEST_TYPE}=` +
							REQUEST_TYPES.MULTI_USER,
					});
				} else {
					history.push(
						getLegacyPath(
							'/RRS/MultiUser_RequestModifyView.aspx?ID=' +
								arrID[0],
						),
						requestees,
					);
				}
			}
		};
		if (rAdvance) {
			advance();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rAdvance]);

	/**
	 * Asynchronously sets session variables anticipated by the Agreement controller.
	 *
	 * @function
	 * @param {Object} requestee custom requestee selection object.
	 * @returns void
	 */
	const prepareSingleUserSubmit = async requestee => {
		await setSession('newTrackingNumMod', 'firstcall');
		await setSession('id', requestee['FI_ID'].Value);
		if (requestee['Employee_Status'].Value.toLowerCase() === 'false') {
			const type = await axiosInstance
				.get('api/Eso/NonEmployeeType', {
					params: {
						ID: requestee['FI_ID'].Value,
					},
				})
				.then(response => {
					switch (response.status) {
						case 200:
							return response.data ? response.data.payload : null;
						default:
							return null;
					}
				})
				.catch(() => null);
			await setSession('UserType', type);
		}
	};

	const onUpload = (response, merge = false) => {
		switch (response.status) {
			case 200:
				switch (response.data.responseCode) {
					case 200:
						let payload = response.data.payload;
						payload.forEach(row => {
							row.ActionCells = [
								{
									BindTo: rActions.findIndex(
										action =>
											action.Type === ACTION_TYPES.REMOVE,
									),
									Checked: false,
									Key: nanoid(),
									Text: 'Delete',
								},
							];
							// generate unique identifier if row invalid
							row.Key = row.Key === '0' ? nanoid() : row.Key;
							// adhere to display expectations
							row.isDisplayed = true;
							if (!checkValidity(row)) {
								row = assignInvalidAction(row);
								if (row.ActionCells.length <= 1) {
									row.ActionCells.push({
										BindTo: rActions.findIndex(
											action =>
												action.Type ===
												ACTION_TYPES.REVERIFY,
										),
										Checked: false,
										Key: nanoid(),
										Text: 'Reverify',
									});
								}
							}
						});
						dispatch(setRows(payload, merge));
						dispatch(
							addRequesteesByFilter({
								filterType: ACTION_FILTER_TYPES.VALID_INCLUDE,
							}),
						);
						dispatch(
							filterRows({
								filterType: ACTION_FILTER_TYPES.VALID_EXCLUDE,
							}),
						);
						setErrorMessage(null);
						break;
					default:
						setErrorMessage(response.data.messages[0].Value);
						break;
				}
				break;
			default:
				break;
		}
	};

	/**
	 * Constructs payload for server-side validation. Payload includes account type and processed owner data.
	 * @param {Array} owners array of owner data.
	 * @returns {Promise<boolean>} indicating validity of payload.
	 */
	const onValidateHandler = async (
		fileIdentifierType = null,
		ids = [],
		merge = false,
	) => {
		let promise = new Promise((resolve, reject) => {
			axiosInstance
				.post(
					'api/Components/UserUpload',
					WrapImsPacket([fileIdentifierType, ...ids]),
				)
				.then(response => {
					onUpload(response, merge);
					resolve(response.data.payload);
				})
				.catch(error => {
					reject(error);
				});
		});
		//TODO - on upload, set the identifier type based on the header
		// promise.catch(error => error).finally(() => setIsLoading(false));
		return promise;
	};

	/**
	 * Method for dispatching action to raise banner with error message.
	 * @param {Object} error error raised.
	 * @returns void.
	 */
	const raiseErrorBanner = error => {
		let message =
			error && error.response && error.response.data
				? error.response.data.payload
				: error.message;
		dispatch(clearMessages());
		dispatch(
			addMessage({
				Key: 3,
				Value: message,
			}),
		);
	};

	const verifyAndProceed = () => {
		const VALIDATION_ACCEPTANCE_INVALID = 0;
		let newErrorMessage = '';
		let valid = true;

		const requesteeLength = sSelectedRequestees
			? sSelectedRequestees.length
			: -1;
		const invalidLength = rRows ? rRows.length : -1;

		if (valid && requesteeLength < VALIDATION_THRESHOLD.MIN) {
			valid = false;
			newErrorMessage = `You have selected too few requestees (${requesteeLength}). Please select additional accounts.`;
		}
		if (
			valid &&
			requesteeLength > 1 &&
			requestType.toLowerCase() === 'service_account'
		) {
			valid = false;
			newErrorMessage = `You have selected too many requestees (${requesteeLength}). Please remove additional accounts.`;
		}
		// applicable only to file upload
		if (
			valid &&
			sTabValue === 1 &&
			invalidLength !== VALIDATION_ACCEPTANCE_INVALID
		) {
			valid = false;
			newErrorMessage = `You have ${invalidLength} invalid identifier(s) from your file upload. Please resolve or remove all of them before proceeding.`;
			setInvalidPopupOpen({
				valid: false,
				errorMessage: `You have ${invalidLength} invalid identifier(s) from your file upload. Would you like to proceed, excluding these identifier(s) from your request?`,
			});
		}

		if (valid) {
			setErrorMessage(null);
			dispatch(advance());
		} else {
			setErrorMessage(newErrorMessage);
		}
	};

	const modal = (
		<Dialog
			open={!invalidPopupOpen.valid}
			onClose={() => {
				setInvalidPopupOpen({
					valid: true,
					errorMessage: '',
					errorSubMessage: '',
				});
			}}
		>
			<DialogTitle>Are you sure?</DialogTitle>
			<DialogContent>{invalidPopupOpen.errorMessage}</DialogContent>
			<DialogActions>
				<Button
					id='requestee-search__invalid-dialog__cancel'
					onClick={() => {
						setInvalidPopupOpen({
							valid: true,
							errorMessage: '',
							errorSubMessage: '',
						});
					}}
					variant='text'
				>
					Cancel
				</Button>
				<Button
					id='requestee-search__invalid-dialog__accept'
					onClick={() => {
						setInvalidPopupOpen({
							valid: true,
							errorMessage: '',
							errorSubMessage: '',
						});
						dispatch(advance());
					}}
					variant='text'
				>
					Proceed
				</Button>
			</DialogActions>
		</Dialog>
	);

	return (
		<Fragment>
			<Base
				loading={rLoading}
				title={'Requestee Selection'}
				helpLink={
					'https://infonet.upmc.com/infonet/ls/content/6254955285853737/using-ims-for-staff-pdf-1764800028'
				}
			>
				{modal}
				<StyleAuxiliary classes={classes[CSS_CLASSES.ROOT_CONTAINER]}>
					<RequesteeSelections headers={multiselectHeaders} />
					<Tabs
							sx={{
								marginBottom: '16px',
								marginTop: '16px',
							}}
							variant='fullWidth'
							onChange={(_, newValue) => {
								dispatch(clearData());
								if (!isSiteAdmin && newValue === 2) {
									dispatch(setRows(rDirectReports));
								} else if (isSiteAdmin && newValue === 1) {
									dispatch(setRows(rDirectReports));
								}
								setTabValue(newValue);
							}}
							value={sTabValue}
						>
							{(isSiteAdmin ? [{ key: 'search', value: 'person_search' }, { key: 'direct reports', value: 'people' }] : sTabIcons).map((tab, index) => (
								<Tab
									id={`requestee-search__tab-${formatForID(
										tab.key,
									)}`}
									iconPosition='start'
									key={index}
									icon={<Icon icon={tab.value} />}
									label={tab.key}
									wrapped
								/>
							))}
						</Tabs>
					<StyleAuxiliary classes={classes[CSS_CLASSES.BODY]}>
						{sTabValue === 0 && (
							<PersonSearch
								errorMessage={sErrorMessage}
								label={isSiteAdmin ? 'Search for other EpicCare Link User accounts in UPMC' : 'Requestee Search'}
								dataTable={{
									actions: rActions,
									headers: rHeaders,
									iconLegend:
										rActions && rActions.length > 0
											? iconLegend.filter(
													icon =>
														rActions[icon.BindTo]
															.Type !==
															ACTION_TYPES.REMOVE &&
														rActions[icon.BindTo]
															.Type !==
															ACTION_TYPES.REVERIFY,
												)
											: [],
									label: 'Search Results',
									onTextFieldChangedHandler: e =>
										dispatch(indexRows(e.target.value)),
									isReadOnly:
										sSelectedRequestees.length >=
										VALIDATION_THRESHOLD.MAX,
									rows: rRows.filter(
										row => row.isDisplayed === true,
									),
								}}
								inputOptions={
									user.isDEVTeam && (
										<StyleAuxiliary
											classes={
												classes[
													CSS_CLASSES.SWITCH_CONTAINER
												]
											}
										>
											<Subtitle1 htmlFor='requestee-search__input-options__include-retired-switch'>
												Include retired
												<br />
												of past 30 days
											</Subtitle1>
											<Switch
												checked={sIsRetiredChecked}
												nativeControlId='requestee-search__input-options__include-retired-switch'
												onChange={e =>
													setIsRetiredChecked(
														e.target.checked,
													)
												}
												data-test={
													ELEMENT_NAMES.SWITCH_INPUT_OPTION
												}
											/>
										</StyleAuxiliary>
									)
								}
								isLoading={sIsSearchLoading}
								multiPerson={true}
								onButtonClickedHandler={payload => {
									setIsSearchLoading(true);
									if (
										requestType.toLowerCase() ===
										'service_account'
									) {
										payload.payload.Target =
											'SERVICE_ACCOUNT';
									}
									payload.payload.Input.Extensions =
										sIsRetiredChecked
											? {
													Days_Retired: 30,
												}
											: null;
									axiosInstance
										.post(
											'api/Components/PersonSearch',
											payload,
										)
										.then(response => {
											switch (response.status) {
												case 200:
													switch (
														response.data
															.responseCode
													) {
														case 200:
															let payload =
																response.data
																	.payload;

															payload.forEach(
																row => {
																	if (
																		!checkValidity(
																			row,
																		)
																	) {
																		row =
																			assignInvalidAction(
																				row,
																			);
																	} else {
																		row.ActionCells =
																			[
																				{
																					BindTo: 0,
																					Checked: false,
																					Key: nanoid(),
																					Text: 'Toggle selection',
																				},
																			];
																	}
																	row.isDisplayed = true;
																	return row;
																},
															);
															dispatch(
																setRows(
																	payload,
																),
															);
															setErrorMessage(
																null,
															);
															break;
														default:
															setErrorMessage(
																response.data
																	.messages[0]
																	.Value,
															);
															break;
													}
													break;
												default:
													break;
											}
										})
										.catch(error => {
											setErrorMessage(
												error &&
													error.response &&
													error.response.data
													? error.response.data
															.payload
													: error.message,
											);
										})
										.finally(() => {
											setIsSearchLoading(false);
										});
								}}
								requestTarget={isSiteAdmin ? 'REQUESTEE_LINK' : 'REQUESTEE'}
								data-test={ELEMENT_NAMES.PERSON_SEARCH}
								shouldAutoScroll
							/>
						)}
						{isSiteAdmin && sTabValue === 1 && (
							!rLoading && rRows.length === 0 ? (
								<p>
									You do not have any direct reports to
									display.
								</p>
							) : (
								<DataTable
									actions={rActions}
									headers={directReportsHeaders}
									loading={rLoading}
									rows={rRows}
									stickyRows={1}
								/>
							)
						)}
						{!isSiteAdmin && sTabValue === 1 && (
							<UserUpload
								label={'Upload Users from File'}
								errorMessage={sErrorMessage}
								onValidateHandler={(fileIdentifierType, ids) =>
									onValidateHandler(fileIdentifierType, ids)
								}
								dataTable={{
									actions: deepCloneObject(rActions).map(
										action => {
											// header action cells not supported
											if (action.Header) {
												action.Header = null;
											}
											return action;
										},
									),
									headers: rHeaders,
									iconLegend: iconLegend,
									label: 'Search Results',
									rows: rRows.filter(
										row => row.isDisplayed === true,
									),
									onTextFieldChangedHandler: (
										e,
										row,
										key,
									) => {
										dispatch(
											updateRowValue({
												value: e.target.value,
												row: row,
												key: key,
											}),
										);
									},
								}}
								onDeleteRow={row => dispatch(deleteRow(row))}
								onReverifyRow={() => {}}
								onUpload={response => onUpload(response)}
							/>
						)}
						{!isSiteAdmin && sTabValue === 2 && (
							<DataTable
								actions={rActions}
								headers={directReportsHeaders}
								loading={
									sSelectedRequestees.length >=
									VALIDATION_THRESHOLD.MAX
								}
								rows={rRows}
								stickyRows={1}
							/>
						)}
					</StyleAuxiliary>
					<StyleAuxiliary classes={classes[CSS_CLASSES.FOOTER]}>
						{user.isDEVTeam && sSelectedRequestees.length > 1 ? (
							<StyleAuxiliary
								classes={classes[CSS_CLASSES.SWITCH_CONTAINER]}
							>
								<Subtitle1 htmlFor='requestee-search__input-options__suppress-emails-switch'>
									Suppress request emails
								</Subtitle1>
								<Switch
									checked={sSuppressEmail}
									nativeControlId='requestee-search__input-options__suppress-emails-switch'
									onChange={e =>
										setSuppressEmail(e.target.checked)
									}
									data-test={
										ELEMENT_NAMES.SWITCH_INPUT_OPTION
									}
								/>
							</StyleAuxiliary>
						) : null}
						<Actions>
							<Button
								id='requestee-search__button__submit'
								disabled={sSelectedRequestees.length < 1}
								onClick={verifyAndProceed}
								variant='outlined'
							>
								Proceed
							</Button>
						</Actions>
					</StyleAuxiliary>
				</StyleAuxiliary>
				{requestType === 'Application' ? (
					<Tooltip
						align={CONTENT_ALIGNMENT.RIGHT}
						content='Create new E-Sign-On'
					>
						<FAB
							exited={rLoading}
							icon={<Icon icon='person_add_alt' />}
							id='requestee-search__button__fab'
							onClick={() => {
								dispatch(setLoading(true));
								history.push(
									getLegacyPath(
										'/ESO/NonEmployee_Agreement.aspx?user=0',
									),
								);
							}}
							text='Create'
						/>
					</Tooltip>
				) : null}
			</Base>
		</Fragment>
	);
};

export { RequesteeSearch };
